//Built-in libraries

//Third party libraries
import { PlusIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import InputError from '@/components/UI/Form/Error';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';

//STP Libraries

import ReactSelect from '@/components/UI/Form/ReactSelect';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import type { ReactSelectOption } from '@/types/Constants';
//Assets and Miscellaneous
import { initialValues, itemsTypes } from './constants';
import type { IProjectBudget } from './types';

//Component Begin

const EstimatedProjectBudget = ({ formValues }: { formValues: any }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editBudgetId, setEditBudgetId] = useState<string>('');
  const [selectedProjectBudget, setProjectBudget] = useState<IProjectBudget>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [budget, setBudget] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setBudget(initialValues);
  };

  const editBudgetHandler = (f: any) => {
    setIsEditing(true);
    const formData = Object.keys(budget).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...budget[next],
          value: f[next],
          isTouched: !!f[next],
        },
      };
    }, {});
    setBudget(formData);
    setEditBudgetId(f.id);
  };

  const updateBudgetHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(budget).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: budget[next].value,
            id: editBudgetId,
          };
        },
        {}
      );
      updateData({
        ...data,
        step2: {
          ...formValues,
          estimatedProjectBudget: data.step2.estimatedProjectBudget.map((p: any) => {
            if (p.id === editBudgetId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
    }
  };

  const removeBudgetHandler = (budget: IProjectBudget) => {
    setShowRemoveConfirm(true);
    setProjectBudget(budget);
  };

  const confirmRemoveBudget = async () => {
    setShowRemoveConfirm(false);
    const filteredBudget = data.step2.estimatedProjectBudget.filter((budget: any) => budget.id !== selectedProjectBudget?.id);
    updateData({
      ...data,
      step2: {
        ...formValues,
        estimatedProjectBudget: filteredBudget,
      },
    });

    if (selectedProjectBudget?.id === editBudgetId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveBudget = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...budget };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setBudget((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddBudgetHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(budget).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: budget[next].value };
        },
        {}
      );
      updateData({
        ...data,
        step2: {
          ...formValues,
          estimatedProjectBudget: [...data.step2.estimatedProjectBudget, { ...formattedData, id: uuidv4() }],
        },
      });
      resetForm();
    }
  };

  const inputChangedHandler = createInputChangedHandler(setBudget, budget);

  const calculateTotalFunds = (data: any) => {
    let total = 0;
    if (data.stateGrant) {
      total += data.stateGrant;
    }

    if (data.otherFunds) {
      total += data.otherFunds;
    }
    return total;
  };

  useEffect(() => {
    setBudget((prevState) => ({
      ...prevState,
      totalFunds: {
        ...prevState.totalFunds,
        value: calculateTotalFunds({
          stateGrant: budget?.stateGrant?.value,
          otherFunds: budget?.otherFunds?.value,
        }),
        isTouched: true,
      },
    }));
  }, [budget?.stateGrant?.value, budget?.otherFunds?.value, calculateTotalFunds]);

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove budget'
          message={`You are about to remove ${selectedProjectBudget?.item} budget. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveBudget}
          decline={cancelRemoveBudget}
        />
      )}
      <section className='space-y-4'>
        <h3>Total Estimated Project Budget</h3>

        {data.step2?.estimatedProjectBudget.length > 0 && (
          <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
            <table className='divide-y divide-gray-200 w-full'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Item</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>State Grant ($)</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Other Funds ($)</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Total Costs ($)</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'></th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {data.step2.estimatedProjectBudget.map((budget: IProjectBudget) => (
                  <tr key={budget.id} className={`${budget.id === editBudgetId ? 'bg-neutralColor-lighter' : ''}`}>
                    <td className='p-4'>{budget.item.label}</td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay value={budget.stateGrant ?? 0} />
                    </td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay value={budget.otherFunds ?? 0} />
                    </td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay value={calculateTotalFunds(budget)} />
                    </td>
                    <td className='flex justify-end gap-4 p-4'>
                      <div className='cursor-pointer hover:text-blue-500' onClick={() => editBudgetHandler(budget)}>
                        Edit
                      </div>
                      <div className='cursor-pointer hover:text-tertiaryColor-normal' onClick={() => removeBudgetHandler(budget)}>
                        Delete
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className='w-full grid grid-cols-6 gap-4'>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <ReactSelect
              label='Item'
              id='item'
              name='item'
              options={itemsTypes.map((item) => ({
                label: item,
                value: item,
              }))}
              value={{
                label: budget.item.value,
                value: budget.item.value,
              }}
              error={budget?.item?.error}
              onChange={(option) => {
                inputChangedHandler((option as ReactSelectOption).value, 'item');
              }}
              onBlur={(_e) => {
                inputChangedHandler(budget.item.value, 'item');
              }}
            />
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='originalAmount'>
              State Grant ($)
            </label>
            <NumberFormat
              name='stateGrant'
              id='stateGrant'
              value={budget.stateGrant.value ?? ''}
              className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
              thousandSeparator={true}
              decimalScale={2}
              valueIsNumericString
              prefix={'$ '}
              maxLength={21}
              onValueChange={(target, s) => {
                if (s.source !== 'prop') {
                  inputChangedHandler(target.floatValue, 'stateGrant');
                }
              }}
              onBlur={(e: any) => {
                inputChangedHandler(budget.stateGrant.value, e.target.name);
              }}
            />
            {budget?.stateGrant?.error && <InputError>{budget?.otherCost?.error}</InputError>}
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='originalAmount'>
              Other Funds ($)
            </label>
            <NumberFormat
              name='otherFunds'
              id='otherFunds'
              value={budget.otherFunds.value ?? ''}
              className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
              thousandSeparator={true}
              decimalScale={2}
              valueIsNumericString
              prefix={'$ '}
              maxLength={21}
              onValueChange={(target, s) => {
                if (s.source !== 'prop') {
                  inputChangedHandler(target.floatValue, 'otherFunds');
                }
              }}
              onBlur={(e: any) => {
                inputChangedHandler(budget.otherFunds.value, e.target.name);
              }}
            />
            {budget?.otherFunds?.error && <InputError>{budget?.otherFunds?.error}</InputError>}
          </div>
        </div>
        {!isEditing && <Button icon={<PlusIcon />} label='Add' type='button' btnStyle='primary' onClick={onAddBudgetHandler} />}
        {isEditing && (
          <div className='flex gap-4'>
            <Button label='Save' type='button' btnStyle='primary' onClick={updateBudgetHandler} />
            <Button
              label='Cancel'
              type='button'
              btnStyle='neutral'
              onClick={() => {
                resetForm();
                setEditBudgetId('');
                setIsEditing(false);
              }}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default EstimatedProjectBudget;
